import { useState } from 'react';

import type {
  AddressFormatCandidate,
  HitlActionRequest,
  HitlActionType,
  HitlItem,
} from '../services/types';

import { Button } from './Button';

export interface AddressFormatCardProps {
  hit: HitlItem;
  onAction: (hitId: string, action: HitlActionRequest) => Promise<void>;
  reviewerId: string;
}

type ReplaceMode = 'in_place' | 'sibling' | 'keep_freeform';

const REPLACE_COPY: Record<
  ReplaceMode,
  { label: string; detail: string }
> = {
  in_place: {
    label: 'Replace with parsed components',
    detail:
      'Drop the freeform column and expand into per-component sibling columns (house_number, road, city, postcode, …). Best for downstream analysis — addresses become structured data.',
  },
  sibling: {
    label: 'Keep original alongside parsed components',
    detail:
      'Preserve the original freeform column AND add per-component sibling columns. Best when the literal needs to stay for audit (downstream might still want to display the original).',
  },
  keep_freeform: {
    label: "Don't expand — keep as freeform",
    detail:
      'Preserve the column unchanged. Use when the column is misclassified as addresses or the parser quality is too low to trust. Suppresses this HITL on subsequent runs of the same file.',
  },
};

const REPLACE_ORDER: ReplaceMode[] = ['in_place', 'sibling', 'keep_freeform'];

/**
 * v0.5 Stage F.1 — UI card for the ``address_format`` HITL.
 *
 * Fired by ``cleaners/spatial_cleaner.py::_clean_address_column``
 * when libpostal's parse success rate (rows with ≥ 2 of
 * {house_number, road, postcode, city}) is below 80%.
 *
 * Two flavours of the same surface:
 *
 * * **libpostal available, low success rate** — column may not
 *   really be addresses, or it's a multi-locale mix. Reviewer picks
 *   ``address_replace`` mode.
 *
 * * **libpostal unavailable** — cleanser ran the regex-only fallback
 *   path. Success rate almost always trips because regex extracts at
 *   most a postcode. The card surfaces ``libpostal_available=False``
 *   so the reviewer can either accept the degraded shape (mark as
 *   ``keep_freeform``) or escalate to ops to install libpostal.
 *
 * Reviewer picks via the ``choose_address_format`` action; backend
 * writes a ``column_policy`` audit row keyed on
 * ``(file_hash, column)``. On the next pipeline tick CPA → MOA's
 * ``_split_address_columns`` stage applies the chosen mode.
 */
export function AddressFormatCard({
  hit,
  onAction,
  reviewerId,
}: AddressFormatCardProps) {
  const candidates = hit.candidates as
    | Array<AddressFormatCandidate>
    | null
    | undefined;
  const candidate =
    candidates && candidates.length > 0 ? candidates[0] : null;

  const [pending, setPending] = useState<HitlActionType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [mode, setMode] = useState<ReplaceMode>(
    (candidate?.suggested_replace as ReplaceMode) || 'in_place',
  );

  const onConfirm = async () => {
    setPending('choose_address_format');
    setError(null);
    try {
      await onAction(hit.hit_id, {
        type: 'choose_address_format',
        payload: { address_replace: mode },
        reviewer_id: reviewerId,
      });
      setResolved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setPending(null);
    }
  };

  const successRate =
    candidate && candidate.non_empty > 0
      ? candidate.parsed_full / candidate.non_empty
      : 0;
  const sample = candidate?.sample_components?.[0];
  const libpostalAvailable = candidate?.libpostal_available ?? false;

  return (
    <li
      data-rule-card="address-format"
      data-resolution-type="address_format"
      className={`rounded-md border bg-paper border-l-4 border-l-warn border-border p-4 space-y-3 ${
        resolved ? 'opacity-60' : ''
      }`}
      aria-busy={pending !== null}
    >
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h3 className="m-0 text-sm font-semibold flex-1">
            <code>{hit.column}</code> · pick address expansion mode
          </h3>
          <span className="text-xs uppercase font-semibold rounded-sm px-2 py-0.5 bg-surface-alt text-muted whitespace-nowrap">
            address format
          </span>
        </div>
        {candidate && (
          <p className="text-xs text-muted m-0">
            Parsed cleanly on{' '}
            <strong className="text-ink">
              {candidate.parsed_full} of {candidate.non_empty}
            </strong>{' '}
            non-empty rows ({(successRate * 100).toFixed(0)}%).{' '}
            {candidate.parsed_partial > 0 && (
              <>
                {candidate.parsed_partial} partial,{' '}
              </>
            )}
            {candidate.unparseable > 0 && (
              <>{candidate.unparseable} unparseable.</>
            )}
          </p>
        )}
        {!libpostalAvailable && (
          <p className="text-[11px] text-warn m-0">
            libpostal isn't available in this environment — only postcodes
            were extracted. Install libpostal for full address parsing.
          </p>
        )}
        {sample && Object.keys(sample).length > 0 && (
          <p className="text-[11px] text-muted m-0">
            Sample components:{' '}
            <span className="font-mono">
              {Object.entries(sample)
                .slice(0, 4)
                .map(([k, v]) => `${k}=${v}`)
                .join(', ')}
            </span>
          </p>
        )}
      </div>

      {resolved ? (
        <p className="text-sm text-muted">Resolved.</p>
      ) : (
        <>
          <fieldset className="space-y-2">
            <legend className="text-xs uppercase tracking-wide text-muted">
              Address replace mode
            </legend>
            {REPLACE_ORDER.map((m) => {
              const copy = REPLACE_COPY[m];
              return (
                <label
                  key={m}
                  className="flex items-start gap-3 rounded border border-border p-2 cursor-pointer hover:bg-surface-alt/30"
                >
                  <input
                    type="radio"
                    name={`address-mode-${hit.hit_id}`}
                    value={m}
                    checked={mode === m}
                    onChange={() => setMode(m)}
                    className="mt-0.5"
                    data-testid={`address-mode-${m}`}
                  />
                  <span className="flex-1">
                    <span className="block font-medium text-sm">
                      {copy.label}
                    </span>
                    <span className="block text-xs text-muted mt-0.5">
                      {copy.detail}
                    </span>
                  </span>
                </label>
              );
            })}
          </fieldset>

          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={pending !== null}
              data-testid="address-format-confirm"
            >
              {pending === 'choose_address_format' ? 'Saving…' : 'Confirm'}
            </Button>
          </div>
        </>
      )}
    </li>
  );
}
